import { Fragment, useState } from 'react';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, IconButton } from '@mui/material';
import { Link as RouterLink, useParams } from 'react-router-dom';

import { APP_LINK_STATE } from '../../Home/navRoutes';
import { COLORS } from '../../IndividualPlan/tokens';
import { CountLink } from '../components/CountLink';
import { CELL_SX, FIRST_CELL_SX, HEAD_ROW_SX, ROW_SX, TABLE_SX, growthCell, levelCell, scoredSample } from '../components/GradeTable';
import { SmallGroupMarker } from '../components/SmallGroupMarker';
import { gradeHref, gradeName, schoolComponentOf, schoolParamToComponent } from '../listKeys';
import type { PeSchoolDetail, ScoreUnit } from '../types';
import { EM_DASH, countWithShare, fmtGrowthIn, levelNoun } from '../utils/drilldownFormat';
import {
  DetailCard,
  DetailStrip,
  DrilldownFrame,
  DrilldownPage,
  DrilldownTip,
  NotFoundPage,
  StudentsLinkCard,
  sameDataSentence,
  type LoadedReport,
} from './DrilldownFrame';

const FOOT =
  'Starting level is the composite level on last year’s ACCESS. The info marker means a small group: one student’s score can move its average a lot, and small groups don’t produce findings.';

const startLabel = (label: string) => `Started at ${label.toLowerCase().replace('level 4 or above', 'level 4+')}`;

function narrativeFor(school: PeSchoolDetail, resetYear: boolean, unit: ScoreUnit, scoresAbsent: boolean): string | undefined {
  if (scoresAbsent || school.small || scoredSample(school, unit) === 0) return undefined;
  if (resetYear) {
    return `${school.school}’s grades are on the new ACCESS scale this year; growth against last year returns with the Spring 2027 results.`;
  }
  const growthOf = (g: { growth: number | null; growth_scale: number | null }) => (unit === 'scale_score' ? g.growth_scale : g.growth);
  const first = school.grades.find((g) => g.count > 0 && !g.small && growthOf(g) != null);
  if (!first) return undefined;
  const name = gradeName(first.grade, first.grade_label);
  const last = unit === 'scale_score' ? first.last_growth_scale : first.last_growth;
  const against = last != null ? `against ${fmtGrowthIn(unit, first.last_growth, first.last_growth_scale)} for ${school.school}’s ${name.toLowerCase()} last year` : 'with no comparable group last year';
  return `${name} grew ${fmtGrowthIn(unit, first.growth, first.growth_scale)}, ${against}. Open a grade to see which starting level moved it.`;
}

function SchoolBody({ loaded, school }: { loaded: LoadedReport; school: PeSchoolDetail }) {
  const { resetYear, settings, generatedOn, unit, report } = loaded;
  const scoresAbsent = report.assessment_data_absent;
  const active = school.active_count ?? school.count;
  const threshold = settings.smallGroupThreshold;
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const toggle = (key: string) => setOpen((prev) => ({ ...prev, [key]: !prev[key] }));

  const tip = school.small
    ? `Small school: fewer than ${threshold} ELs, so it produces no findings in the report. Every number is still shown.`
    : school.caveat
      ? `${school.count} ELs: findings about this school carry a small-group note, because one or two students can move its rates.`
      : null;

  return (
    <DrilldownPage crumbs={[{ label: school.school }]} title={school.school} subtitle={sameDataSentence(generatedOn)}>
      <DetailStrip
        items={[
          { label: 'EL students', value: <CountLink count={school.count} listKey={school.key} /> },
          {
            label: 'Newcomers',
            value: (
              <CountLink count={school.newcomers} listKey={`cohort:newcomer:${school.key}`}>
                {countWithShare(school.newcomers, active)}
              </CountLink>
            ),
          },
          {
            label: 'Long-term ELs',
            value: (
              <CountLink count={school.ltel} listKey={`cohort:ltel:${school.key}`}>
                {countWithShare(school.ltel, active)}
              </CountLink>
            ),
          },
          {
            label: 'Met exit criteria',
            value: scoresAbsent ? (
              EM_DASH
            ) : (
              <CountLink count={school.met} listKey={`${school.key}:met`}>
                {countWithShare(school.met, school.count)}
              </CountLink>
            ),
          },
        ]}
      />
      {tip && <DrilldownTip>{tip}</DrilldownTip>}

      <DetailCard
        title="By grade"
        description={`Each grade at ${school.school} against the same grade here last year. Open a grade to see its starting-level groups; the arrow opens the grade across the district.`}
        foot={FOOT}
        narrative={narrativeFor(school, resetYear, unit, scoresAbsent)}
      >
        <Box data-testid="by-grade-table" sx={TABLE_SX}>
          <Box sx={HEAD_ROW_SX}>
            <Box sx={FIRST_CELL_SX}>Grade</Box>
            <Box sx={{ ...CELL_SX, width: 120 }}>Students</Box>
            <Box sx={{ ...CELL_SX, width: 140 }}>{resetYear ? `${levelNoun(unit) === 'level' ? 'Level' : 'Scale score'} · new scale` : `Composite ${levelNoun(unit)}`}</Box>
            <Box sx={CELL_SX}>Growth this year</Box>
            <Box sx={{ ...CELL_SX, width: 170 }}>Same grade last year</Box>
            <Box sx={{ ...CELL_SX, width: 104 }}>Met exit criteria</Box>
            <Box sx={{ width: 40, flexShrink: 0 }} />
          </Box>
          {school.grades.map((g) => {
            const name = gradeName(g.grade, g.grade_label);
            const expanded = open[g.key] === true;
            return (
              <Fragment key={g.key}>
                <Box sx={ROW_SX}>
                  <Box sx={FIRST_CELL_SX}>
                    <Box
                      component="button"
                      type="button"
                      aria-expanded={expanded}
                      onClick={() => toggle(g.key)}
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                        p: 0,
                        border: 0,
                        bgcolor: 'transparent',
                        font: 'inherit',
                        fontWeight: 600,
                        color: COLORS.ink,
                        cursor: 'pointer',
                      }}
                    >
                      <ExpandMoreIcon
                        sx={{ fontSize: 18, transform: expanded ? 'none' : 'rotate(-90deg)', transition: 'transform 0.12s ease' }}
                      />
                      {name}
                    </Box>
                  </Box>
                  <Box sx={{ ...CELL_SX, width: 120 }}>
                    <CountLink count={g.count} listKey={g.key} />
                    {g.small && g.count > 0 && <SmallGroupMarker threshold={threshold} />}
                  </Box>
                  <Box sx={{ ...CELL_SX, width: 140 }}>{levelCell(g.level, g.level_scale, unit)}</Box>
                  <Box sx={CELL_SX}>{scoresAbsent ? EM_DASH : growthCell(g.growth, g.growth_scale, unit, resetYear, scoredSample(g, unit))}</Box>
                  <Box sx={{ ...CELL_SX, width: 170, color: COLORS.muted }}>
                    {scoresAbsent ? EM_DASH : growthCell(g.last_growth, g.last_growth_scale, unit, resetYear, 0)}
                  </Box>
                  <Box sx={{ ...CELL_SX, width: 104 }}>{scoresAbsent ? EM_DASH : g.met}</Box>
                  <Box sx={{ width: 40, flexShrink: 0, display: 'flex', justifyContent: 'flex-end' }}>
                    <IconButton
                      component={RouterLink}
                      to={gradeHref(g.grade)}
                      state={APP_LINK_STATE}
                      size="small"
                      aria-label={`${name} across the district`}
                      sx={{ color: COLORS.link }}
                    >
                      <ChevronRightIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                  </Box>
                </Box>
                {expanded &&
                  g.starts
                    .filter((s) => s.count > 0)
                    .map((s) => (
                      <Box key={s.key} sx={{ ...ROW_SX, bgcolor: COLORS.panel, pl: 5 }}>
                        <Box sx={FIRST_CELL_SX}>{startLabel(s.label)}</Box>
                        <Box sx={{ ...CELL_SX, width: 120 }}>
                          <CountLink count={s.count} listKey={s.key} />
                          {s.small && s.count > 0 && <SmallGroupMarker threshold={threshold} />}
                        </Box>
                        <Box sx={{ ...CELL_SX, width: 140 }} />
                        <Box sx={CELL_SX}>{resetYear || scoresAbsent ? EM_DASH : s.bucket === 'none' ? (scoredSample(s, unit) > 0 ? 'First year tested' : EM_DASH) : growthCell(s.growth, s.growth_scale, unit, false, scoredSample(s, unit))}</Box>
                        <Box sx={{ ...CELL_SX, width: 170, color: COLORS.muted }}>
                          {resetYear || scoresAbsent || s.bucket === 'none' ? EM_DASH : growthCell(s.last_growth, s.last_growth_scale, unit, false, 0)}
                        </Box>
                        <Box sx={{ ...CELL_SX, width: 104 }}>{scoresAbsent ? EM_DASH : s.met}</Box>
                        <Box sx={{ width: 40, flexShrink: 0 }} />
                      </Box>
                    ))}
              </Fragment>
            );
          })}
        </Box>
      </DetailCard>

      <StudentsLinkCard count={school.count} listKey={school.key} noun="at this school" population="served" />
    </DrilldownPage>
  );
}

export function SchoolDetailPage() {
  const { school: param = '' } = useParams<{ school: string }>();
  const crumbs = [{ label: param }];
  return (
    <DrilldownFrame crumbs={crumbs}>
      {(loaded) => {
        const component = schoolParamToComponent(param);
        const school = loaded.drilldown.schools.find((s) => schoolComponentOf(s.key) === component);
        if (!school) {
          return (
            <NotFoundPage
              crumbs={crumbs}
              loaded={loaded}
              message={`${param} has no EL students in ${loaded.schoolYear}.`}
            />
          );
        }
        return <SchoolBody loaded={loaded} school={school} />;
      }}
    </DrilldownFrame>
  );
}
